// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { _x } from '@wordpress/i18n';

/**
 * Creates a modal
 */
export default class Modal {
	/**
	 * Gets/saves the required data and invokes the methods to
	 * create the modal
	 *
	 * @param {Object}      content                  The content of the modal
	 * @param {String}      content.title            The title
	 * @param {String}      content.warning          The warning
	 * @param {Object}      content.buttons          The buttons
	 * @param {String}      content.buttons.cancel   The label of the cancel button
	 * @param {String}      content.buttons.confirm  The label of the confirm button
	 * @param {String|Node} content.content          The modal body
	 * @param {Object}      content.events           Object with callbacks
	 * @param {Function}    content.events.onConfirm Callback invoked when user press the confirm button
	 * @param {Function}    content.events.onCancel  Callback invoked when user press the cancel button
	 * @param {Function}    content.events.onShow    Callback invoked when the modal is displayed
	 * @param {Function}    content.events.onHide    Callback invoked when the modal is hidden
	 * @param {Object}      config                   Configuration object
	 * @param {Integer}     config.fadeDuration      Fade animation duration when the modal is displayed or hidden
	 * @param {String}      config.size              The size of the modal: 'small', 'medium', 'large', 'extra-large'
	 * @param {Boolean}     config.warningFirst      True if the warning should go above the modal content
	 * @return {undefined}
	 */
	constructor( content = {}, config = {} ) {
		// Create elements object
		this.elements = {
			container: null,
			automatorContainer: null, // ( .uap ) This DIV is only created to avoid conflicts with other plugins
			modal: {
				container: null,
				box: {
					container: null,
					title: null,
					warning: null,
					buttons: {
						container: null,
						cancel: null,
						confirm: null,
					},
				},
			},
		};

		// Set container
		this.elements.container = $( 'body' );

		// Set isVisible property
		this.isVisible = false;

		// Define default configuration
		this.config = $.extend(
			true,
			{
				fadeDuration: 150,
				size: 'medium',
				warningFirst: false,
				customClass: '',
				allowContentOverflow: false
			},
			config
		);

		// Save modal box content. Add default values
		this.content = $.extend(
			{},
			{
				buttons: {
					cancel: escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ),
				},
			},
			content
		);

		// Create new modal box
		this.create();

		// Return object
		return this;
	}

	/**
	 * Creates the modal box DOM elements
	 *
	 * @return {undefined}
	 */
	create() {
		const automatorContainer = $( '<div/>', { class: 'uap' } );
		const modal = $( '<div/>', { class: 'modal' } );
		const modalBox = $( '<div/>', { class: 'modal__box' } );

		// Check if the user wants a different modal size than the default size
		if (
			isDefined( this.config.size ) &&
			[ 'small', 'medium', 'large', 'extra-large', 'xx-large' ].includes( this.config.size )
		) {
			// Add the correct size class
			modal.addClass( `modal--${ this.config.size }` );
		}

		// Check if we should allow content overflow
		if ( this.config.allowContentOverflow ){
			modal.addClass( `modal--allow-content-overflow` );
		}

		// Check if the user wants to add a custom class
		if ( isDefined( this.config.customClass ) ) {
			modal.addClass( `modal--${ this.config.customClass }` );
		}

		// Save elements
		this.elements.automatorContainer = automatorContainer;
		this.elements.modal.container = modal;
		this.elements.modal.box.container = modalBox;

		// Set title
		if ( isDefined( this.content.title ) ) {
			const modalTitle = $( '<div/>', { class: 'modal__title' } ).html( this.content.title );
			modalBox.append( modalTitle );

			// Save element
			this.elements.modal.box.title = modalTitle;
		}

		// Set content
		let modalContent;
		if ( isDefined( this.content.content ) ) {
			modalContent = $( '<div/>', { class: 'modal__content' } );

			// Check if the content is node element
			try {
				modalContent.append( this.content.content );
			} catch ( e ) {
				modalContent.html( this.content.content );
			}

			modalBox.append( modalContent );

			// Save element
			this.elements.modal.box.content = modalContent;
		}

		// Set warning
		let modalWarning;
		if ( isDefined( this.content.warning ) ) {
			modalWarning = $( '<div/>', { class: 'modal__warning' } ).html( this.content.warning );

			// Save element
			this.elements.modal.box.warning = modalWarning;
		}

		// Check the order of the warning
		if ( isDefined( this.config.warningFirst ) && this.config.warningFirst ) {
			if ( isDefined( this.content.warning ) ) {
				modalBox.append( modalWarning );
			}

			if ( isDefined( this.content.content ) ) {
				modalBox.append( modalContent );
			}
		} else {
			if ( isDefined( this.content.content ) ) {
				modalBox.append( modalContent );
			}

			if ( isDefined( this.content.warning ) ) {
				modalBox.append( modalWarning );
			}
		}

		// Set buttons
		const modalButtons = $( '<div/>', {
			class: 'modal__buttons',
		} );
		const cancelButton = $( '<button/>', {
			class: 'uap-btn uap-btn--secondary',
		} ).html( this.content.buttons.cancel );
		modalButtons.append( cancelButton );

		// Save elements
		this.elements.modal.box.buttons.container = modalButtons;
		this.elements.modal.box.buttons.cancel = cancelButton;

		if ( isDefined( this.content.buttons.confirm ) ) {
			const confirmButton = $( '<button/>', {
				class: 'uap-btn uap-btn--primary',
			} ).html( this.content.buttons.confirm );
			modalButtons.append( confirmButton );

			// Save element
			this.elements.modal.box.buttons.confirm = confirmButton;
		}

		modalBox.append( modalButtons );

		// Append elements and add to the container
		modal.append( modalBox );
		automatorContainer.append( modal );
		this.elements.container.append( automatorContainer );

		// Add open class to the html tag
		$( 'html' ).addClass( 'automator-modal-open' );

		// Bind
		this.bind();
	}

	/**
	 * Defines custom events
	 * This adds the ability to invoke callbacks when:
	 * - After the events are set
	 * - After the modal is displayed
	 * - After the modal is hidden
	 * - When the user clicks on the confirmation button
	 * - When the user clicks on the cancel button
	 *
	 * @param {Object} events Object with callbacks
	 * @return {undefined}
	 */
	setEvents( events ) {
		// Define events
		this.events = {
			// eslint-disable-next-line no-empty-function, require-jsdoc
			afterSetEvents: isDefined( events.afterSetEvents ) ? events.afterSetEvents : () => {},
			// eslint-disable-next-line no-empty-function, require-jsdoc
			afterShow: isDefined( events.afterShow ) ? events.afterShow : () => {},
			// eslint-disable-next-line no-empty-function, require-jsdoc
			afterHide: isDefined( events.afterHide ) ? events.afterHide : () => {},
			// eslint-disable-next-line no-empty-function, require-jsdoc
			onConfirm: isDefined( events.onConfirm ) ? events.onConfirm : () => {},
			// eslint-disable-next-line no-empty-function, require-jsdoc
			onCancel: isDefined( events.onCancel ) ? events.onCancel : () => {},
		};

		if ( ! isDefined( events.onCancel ) ) {
			// eslint-disable-next-line no-empty-function, require-jsdoc
			this.events.onCancel = () => {
				// Default onCancel function
				// Hide modal box
				this.hide();
			};
		}

		// Fire afterSetEvents event
		this.events.afterSetEvents();
	}

	/**
	 * Removes the modal from the DOM
	 *
	 * @return {undefined}
	 */
	destroy() {
		const thisModal = this;

		// Unbind everything
		this.unbind();

		// Check if it's visible
		if ( this.isVisible ) {
			// We have to hide it first
			this.hide( () => {
				thisModal.elements.automatorContainer.remove();
			} );
		} else {
			thisModal.elements.automatorContainer.remove();
		}

		// Remove modal open class from the html tag
		$( 'html' ).removeClass( 'automator-modal-open' );
	}

	/**
	 * Listens clicks to the confirm and cancel buttons
	 *
	 * @return {undefined}
	 */
	bind() {
		const thisModal = this;

		// Bind cancel button
		if ( isDefined( this.elements.modal.box.buttons.cancel ) ) {
			this.elements.modal.box.buttons.cancel.on( 'click', () => {
				thisModal.events.onCancel();
			} );
		}

		// Bind confirm button
		if ( isDefined( this.elements.modal.box.buttons.confirm ) ) {
			this.elements.modal.box.buttons.confirm.on( 'click', () => {
				thisModal.events.onConfirm();
			} );
		}
	}

	/**
	 * Stops listening clicks on the confirm and cancel buttons
	 *
	 * @return {undefined}
	 */
	unbind() {
		if ( isDefined( this.elements.modal.box.buttons.cancel ) ) {
			this.elements.modal.box.buttons.cancel.unbind( 'click' );
		}

		if ( isDefined( this.elements.modal.box.buttons.confirm ) ) {
			this.elements.modal.box.buttons.confirm.unbind( 'click' );
		}
	}

	/**
	 * Shows the modal
	 *
	 * @param {Function} callback Custom function that it's going to be
	 *                            invoked after we show the modal
	 * @return {undefined}
	 */
	show( callback = null ) {
		const thisModal = this;

		// eslint-disable-next-line no-empty-function, require-jsdoc
		let event = () => {};

		if ( isDefined( callback ) ) {
			event = callback;
		} else if ( isDefined( this.events.afterShow ) ) {
			event = this.events.afterShow;
		}

		callback = event;

		// Change is visible status
		this.isVisible = true;

		// Fade in
		this.elements.modal.container.fadeIn( thisModal.config.fadeDuration, callback );
	}

	/**
	 * Hides the modal
	 *
	 * @param {Function} callback Custom function that it's going to be
	 *                            invoked after we hide the modal
	 * @return {undefined}
	 */
	hide( callback = null ) {
		// eslint-disable-next-line no-empty-function, require-jsdoc
		let event = () => {};

		if ( isDefined( callback ) ) {
			event = callback;
		} else {
			event = this.events.afterHide;
		}

		callback = event;

		// Change is visible status
		this.isVisible = false;

		// Fade out
		this.elements.modal.container.fadeOut( this.config.fadeDuration, callback );

		// Remove modal open class from the html tag
		$( 'html' ).removeClass( 'automator-modal-open' );
	}

	/**
	 * Returns all the DOM elements related to the modal
	 *
	 * @return {Object} All the DOM elements created by this class
	 */
	getElements() {
		return this.elements;
	}
}
